/**
 * Service-layer wrappers around `/v1/golab/snapshots/...` (B6).
 *
 * Each function calls `fetchWithAuth` and returns the decoded JSON shape
 * verbatim. Snapshot status arrives as the hyphenated `rolled-back` form
 * per `feature-spec.md` §5.8 — the FE consumes that directly, no further
 * translation needed.
 */

import { fetchWithAuth } from '../../../../../utils/api';
import type {
  CreateSnapshotPayload,
  ImpactPayload,
  ImpactResult,
  ListPaginationParams,
  RollbackResult,
  Snapshot,
  SnapshotList,
} from '../types';

const BASE_PATH = '/v1/golab/snapshots';

/** List snapshots for the caller's org, newest first. Paginated. */
export const listSnapshots = async (
  params?: ListPaginationParams,
): Promise<SnapshotList> => {
  const sp = new URLSearchParams();
  if (params?.limit != null) sp.append('limit', String(params.limit));
  if (params?.offset != null) sp.append('offset', String(params.offset));
  const q = sp.toString();
  return fetchWithAuth(`${BASE_PATH}${q ? `?${q}` : ''}`);
};

/**
 * Capture a new snapshot of the org's annotation state. Archives the prior
 * active snapshot. 404 if no live taxonomy exists for `taxonomyKey`;
 * 400 if version/label is empty.
 */
export const createSnapshot = async (
  payload: CreateSnapshotPayload,
): Promise<Snapshot> =>
  fetchWithAuth(BASE_PATH, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/**
 * Restore `snapshotId` as the active snapshot. Marks newer snapshots as
 * `rolled-back`, reverts non-gold records to `pending`, and flips
 * `is_current=false` on those records' annotations + resolutions
 * (annotation history is preserved). 400 if target is already active.
 */
export const rollbackSnapshot = async (
  snapshotId: string,
): Promise<RollbackResult> =>
  fetchWithAuth(`${BASE_PATH}/${snapshotId}/rollback`, {
    method: 'POST',
  });

/**
 * Compute the re-annotation impact of a taxonomy edit (B12-BE).
 *
 * Body carries both the previous and new `taxonomyData` blobs; the BE runs
 * `TaxonomyDiffService.compute_diff` server-side and returns the affected
 * dimension list plus three count buckets that drive the FE's
 * Re-annotation Impact modal (`feature-spec.md` §3.6.3).
 */
export const computeImpact = async (
  payload: ImpactPayload,
): Promise<ImpactResult> =>
  fetchWithAuth(`${BASE_PATH}/impact`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });


/** Aggregator export — dual-export convention matching `annotationService.ts`. */
export const snapshotService = {
  listSnapshots,
  createSnapshot,
  rollbackSnapshot,
  computeImpact,
};
